import { createDefaultPanel } from 'features/charts-v2/store'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import {
  YOGIQL_QUERY_FIELDS,
  type YogiQLStoredPanel,
  isYogiQLStoredPanel,
  type PanelQueryFields,
  type StoredPanel,
} from 'features/charts-v2/types/stored-panel'
import type {
  ChartRequest,
  FieldCatalogResponse,
} from 'features/charts-v2/types/yogiql'
import { fromYogiQLChartRequest } from 'features/charts-v2/utils/from-yogiql-chart-request'
import { normalizeLegacyPanelSearch } from 'features/charts-v2/utils/normalize-legacy-panel-search'
import { resolveYogiQLNames } from 'features/charts-v2/utils/resolve-yogiql-names'
import {
  type YogiQLEncoding,
  toYogiQLChartRequest,
} from 'features/charts-v2/utils/to-yogiql-chart-request'
import { warnYogiQLFallback } from 'features/charts-v2/utils/warn-yogiql-fallback'
import type { FilterOption } from 'features/filters/types'

/** What reading a stored panel needs: the hub's filter options. */
export type StoredPanelReadContext = {
  filterOptions: FilterOption[]
}

/** What writing one needs on top: the hub's field catalog. */
export type StoredPanelWriteContext = StoredPanelReadContext & {
  catalog: FieldCatalogResponse
}

const queryFields = (panel: ChartPanel): PanelQueryFields =>
  Object.fromEntries(
    YOGIQL_QUERY_FIELDS.map((key) => [key, panel[key]]),
  ) as PanelQueryFields

const withoutQueryFields = (
  panel: ChartPanel,
): Omit<YogiQLStoredPanel, 'request'> => {
  const rest: Record<string, unknown> = { ...panel }
  for (const key of YOGIQL_QUERY_FIELDS) delete rest[key]
  return rest as Omit<YogiQLStoredPanel, 'request'>
}

/** The request a panel is stored as, or why it keeps the legacy form. A
 *  request is stored only if it reads back into a panel that serialises to
 *  the same request, so a saved chart always reloads as itself. */
export function storedRequest(
  panel: ChartPanel,
  context: StoredPanelWriteContext,
): ChartRequest | string {
  const { catalog, filterOptions } = context
  const encode = (p: ChartPanel): YogiQLEncoding =>
    toYogiQLChartRequest(
      p,
      catalog,
      resolveYogiQLNames(p, filterOptions),
      'store',
    )
  const normalized = normalizeLegacyPanelSearch(panel)
  const encoded = encode(normalized)
  if (!encoded.ok) return encoded.reason
  const decoded = fromYogiQLChartRequest(encoded.request, filterOptions)
  if (!decoded.ok) return `stored request does not read back: ${decoded.reason}`
  const again = encode({ ...normalized, ...decoded.fields })
  if (
    !again.ok ||
    JSON.stringify(again.request) !== JSON.stringify(encoded.request)
  ) {
    return 'stored request does not read back as itself'
  }
  return encoded.request
}

/** A panel as a store saves it under the flag: the YogiQL form when it
 *  round-trips, else the panel unchanged (06 §30). */
export function toStoredPanel(
  panel: ChartPanel,
  context: StoredPanelWriteContext,
): StoredPanel {
  const request = storedRequest(panel, context)
  if (typeof request === 'string') {
    warnYogiQLFallback(`panel saved in the legacy form: ${request}`)
    return panel
  }
  return { ...withoutQueryFields(panel), request }
}

/**
 * The query fields a stored request reads back into, or why it does not read.
 * Values the hub no longer offers are dropped, with a warning.
 */
export function storedQueryFields(
  request: ChartRequest,
  context: StoredPanelReadContext,
): PanelQueryFields | string {
  const decoded = fromYogiQLChartRequest(request, context.filterOptions)
  if (!decoded.ok) return decoded.reason
  if (decoded.dropped.length) {
    warnYogiQLFallback(
      `stored filter values no longer in this hub: ${decoded.dropped.join(', ')}`,
    )
  }
  return decoded.fields
}

/**
 * A stored panel as the page edits it. A legacy panel passes through; a
 * YogiQL one reads its request back into the query fields. A request that does
 * not read (the registry moved on, or the hub's filter options did not load: a
 * null context) keeps the panel's display state over the default chart, with a
 * warning, rather than failing the whole load. The stored hash is untouched.
 */
export function fromStoredPanel(
  stored: StoredPanel,
  context: StoredPanelReadContext | null,
): ChartPanel {
  if (!isYogiQLStoredPanel(stored)) return stored
  const { request, ...rest } = stored
  const fields = context
    ? storedQueryFields(request, context)
    : 'the filter options did not load'
  if (typeof fields === 'string') {
    warnYogiQLFallback(`stored chart did not load: ${fields}`)
    return { ...rest, ...queryFields(createDefaultPanel(rest.order)) }
  }
  return { ...rest, ...fields }
}

/** Whether any panel is in the YogiQL form, so a reader needs the context. */
export const hasYogiQLPanels = (panels: unknown): boolean =>
  Array.isArray(panels) && panels.some(isYogiQLStoredPanel)
